/**
 * Monaco inlay hints provider for INSERT INTO ... VALUES statements.
 * Shows column names as ghost text before each value in the VALUES clause.
 */

import { parseInsertForHints, extractPlainTableName, parseQualifiedTableName } from './insertHintParser';
import { getCachedPgSchema } from '../../../connections/services/language-services/pgSchemaCache';
import { getCachedMysqlSchema } from '../../../connections/services/language-services/mysqlSchemaCache';
import { getCachedSqliteSchema } from '../../../connections/services/language-services/sqliteSchemaCache';
import { getCachedOracleSchema } from '../../../connections/services/language-services/oracleSchemaCache';
import {
  getCachedColumns as getCachedResolvedColumns,
  fetchColumns as fetchResolvedColumns,
} from './insertHintColumnResolver';
import type { DatabaseEngineId } from '@/core/engines';

type SchemaColumn = { name: string; dataType: string };

/**
 * Look up column names for a table from the cached schema snapshot.
 * Returns null if no cache is available or the table is not found.
 *
 * Used by engines with a local schema cache (PG, MySQL, SQLite, Oracle). MSSQL
 * resolves columns via the lazy `insertHintColumnResolver` instead — see
 * `computeInsertInlayHints` below.
 */
export function getColumnsFromSchemaCache(
  connectionId: string,
  engineId: DatabaseEngineId,
  tableName: string,
): SchemaColumn[] | null {
  const plainName = extractPlainTableName(tableName).toLowerCase();

  switch (engineId) {
    case 'pgsql': {
      const snapshot = getCachedPgSchema(connectionId);
      return snapshot?.columnsByTableLower[plainName] ?? null;
    }
    case 'mysql': {
      const snapshot = getCachedMysqlSchema(connectionId);
      return snapshot?.columnsByObjectNameLower[plainName] ?? null;
    }
    case 'sqlite': {
      const snapshot = getCachedSqliteSchema(connectionId);
      return snapshot?.columnsByObjectNameLower[plainName] ?? null;
    }
    case 'oracle': {
      const snapshot = getCachedOracleSchema(connectionId);
      return snapshot?.columnsByObjectNameLower[plainName] ?? null;
    }
    case 'mock': {
      // Mock engine (E2E tests): check PG cache as the test-injected store
      const snapshot = getCachedPgSchema(connectionId);
      return snapshot?.columnsByTableLower[plainName] ?? null;
    }
    default:
      // MSSQL falls through — it has no local schema cache. The async path in
      // computeInsertInlayHints handles MSSQL via the lazy column resolver.
      return null;
  }
}

type InlayHint = {
  position: { lineNumber: number; column: number };
  label: string;
  kind: 'parameter';
  paddingRight: boolean;
};

/**
 * Resolve a column list for MSSQL via the lazy per-table resolver. Tries the
 * cache first so a warm cache is effectively synchronous; falls back to an
 * awaited IPC fetch otherwise.
 */
async function resolveMssqlColumns(
  connectionId: string,
  database: string,
  tableName: string,
): Promise<string[] | null> {
  const qualified = parseQualifiedTableName(tableName);
  const params = {
    connectionId,
    database,
    schema: qualified.schema ?? 'dbo',
    table: qualified.table,
  };

  const cached = getCachedResolvedColumns(params);
  if (cached) return cached;

  return fetchResolvedColumns(params);
}

/**
 * Shared column-name resolver used by both the inlay-hints provider and the
 * signature-help provider.
 *
 * Resolution priority:
 *   1. `parsedColumnNames` — if non-null, return as-is (fully synchronous).
 *   2. Engine-specific local schema cache (PG/MySQL/SQLite/Oracle/mock — sync).
 *   3. MSSQL lazy IPC resolver with cache (async; warm cache is synchronous).
 *
 * Returns null when no column names can be resolved.
 */
export async function resolveInsertColumnNames(
  tableName: string,
  parsedColumnNames: string[] | null,
  connectionId: string | undefined,
  database: string | undefined,
  engineId: DatabaseEngineId,
): Promise<string[] | null> {
  if (parsedColumnNames) return parsedColumnNames;

  if (!connectionId) return null;

  const schemaColumns = getColumnsFromSchemaCache(connectionId, engineId, tableName);
  if (schemaColumns) {
    return schemaColumns.map((c) => c.name);
  }

  if (engineId === 'mssql' && database) {
    return await resolveMssqlColumns(connectionId, database, tableName);
  }

  return null;
}

/**
 * Compute inlay hints for INSERT ... VALUES statements in the given text.
 *
 * Resolution priority:
 *   1. Explicit column list in the INSERT statement (sync, no lookup).
 *   2. Engine-specific local schema cache (PG/MySQL/SQLite/Oracle — sync).
 *   3. Lazy IPC fetch via `insertHintColumnResolver` (MSSQL — async with cache).
 */
export async function computeInsertInlayHints(
  text: string,
  connectionId: string | undefined,
  database: string | undefined,
  engineId: DatabaseEngineId,
  offsetToPosition: (offset: number) => { lineNumber: number; column: number },
): Promise<InlayHint[]> {
  const parsed = parseInsertForHints(text);
  if (!parsed) return [];

  const columnNames = await resolveInsertColumnNames(
    parsed.tableName,
    parsed.columnNames,
    connectionId,
    database,
    engineId,
  );

  if (!columnNames) return [];

  const hints: InlayHint[] = [];

  for (const tuple of parsed.tuples) {
    for (let i = 0; i < tuple.length; i++) {
      const valueHint = tuple[i];
      const colName = columnNames[i];
      if (!colName) continue;

      const pos = offsetToPosition(valueHint.valueOffset);
      hints.push({
        position: pos,
        label: `${colName}:`,
        kind: 'parameter',
        paddingRight: true,
      });
    }
  }

  return hints;
}
